import clsx from 'clsx'
import React, { FC } from 'react'

import Check from '@Assets/SVG/Check'
import { SVGIcon } from '@Components/Icons'
import { TRUST_PILOT_WIDGET_TEMPLATE_MICRO_STAR, TrustPilotWidget } from '@Components/TrustPilotWidget'
import { Money } from '@Generated/graphql'
import { useFreeShippingThreshold } from '@Globals/Metadata/Hooks'
import { useTranslation } from '@Globals/Translations'

import { DeliveryCartField } from '../CartV2/Components/CartTotals/FieldListing/Fields/DeliveryCartField/DeliveryCartField'
import styles from './FreeShippingCountdown.module.scss'
import stylesV2 from './FreeShippingCountdownV2.module.scss'

export interface FreeShippingCountdownProps {
  expectedDelivery: string
  isFree: boolean
  remaining?: Money
  showFreeShippingProgress?: boolean
  v2?: boolean
}

export const FreeShippingCountdown: FC<FreeShippingCountdownProps> = ({ expectedDelivery, isFree, remaining, showFreeShippingProgress, v2 }) => {
  const { t, tDanger } = useTranslation()
  const freeShippingThreshold = useFreeShippingThreshold()

  const percentageLeft = ((remaining?.value || 0) / (freeShippingThreshold?.value || 0)) * 100

  if (v2) {
    return (
      <div className={clsx(stylesV2.wrapper, 'fullwidth')}>
        {isFree && showFreeShippingProgress && (
          <div className={clsx(stylesV2.progressHeader, stylesV2.completed)}>{t('cart:WB_YOU_EARNED_FREE_SHIPPING')}</div>
        )}
        {!isFree && remaining && remaining.value > 0 && showFreeShippingProgress && (
          <div
            className={stylesV2.progressHeader}
            {...tDanger('cart:WB_XX_AWAY_FROM_FREE_SHIPPING', {
              arg1: remaining.format,
            })}
          />
        )}
        <DeliveryCartField expectedDelivery={expectedDelivery} v2 />
      </div>
    )
  }

  return (
    <div className={styles.freeShipping}>
      <div className={styles.content}>
        {showFreeShippingProgress && isFree && (
          <>
            <SVGIcon className={styles.icon} icon={Check} />
            <span className={styles.text}>{t('cart:WB_YOU_EARNED_FREE_SHIPPING')}</span>
          </>
        )}

        {!isFree && remaining && remaining.value > 0 && showFreeShippingProgress && (
          <div className={styles.progressContainer}>
            <span
              className={styles.text}
              {...tDanger('cart:WB_XX_AWAY_FROM_FREE_SHIPPING', {
                arg1: remaining.format,
              })}
            />
            <div
              className={styles.bar}
            >
              <div className={styles.progress} style={{ width: `${100 - percentageLeft}%` }} />
            </div>
          </div>
        )}
      </div>

      <DeliveryCartField className={styles.expected} expectedDelivery={expectedDelivery} iconClassName={styles.deliveryIcon} />
      <div className={styles.trustWrapper}>
        <TrustPilotWidget className={styles.trust} trustPilotWidgetTemplate={TRUST_PILOT_WIDGET_TEMPLATE_MICRO_STAR} />
      </div>
    </div>
  )
}
// TODO: Use this once BE is ready with new calculation, See ENG-989 task 7 and 9 in UAT comments
export const FreeShippingProgress: FC<{
  isFree: boolean
  remaining?: Money
  showFreeShippingProgress?: boolean
}> = ({ isFree, remaining, showFreeShippingProgress }) => {
  const { t, tDanger } = useTranslation()
  const freeShippingThreshold = useFreeShippingThreshold()
  const percentageLeft = ((remaining?.value || 0) / (freeShippingThreshold?.value || 0)) * 100
  return (
    <div className={clsx(styles.slimProgress)}>
      {showFreeShippingProgress && isFree && (
        <span className={styles.wrap}>
          <span className={styles.text}>{t('cart:WB_YOU_EARNED_FREE_SHIPPING')}</span>
        </span>
      )}

      {!isFree && remaining && remaining.value > 0 && showFreeShippingProgress && (
        <span
          className={styles.wrap}
          {...tDanger('cart:WB_XX_AWAY_FROM_FREE_SHIPPING', {
            arg1: remaining.format,
          })}
        />
      )}
      <div className={styles.progress}>
        <div className={styles.indicator} style={{ width: `${100 - percentageLeft}%` }} />
      </div>
    </div>
  )
}
